import React from "react";
import { RouterProvider } from "react-router-dom";
import { useDispatch } from "react-redux";
import UserInteractionOverlay from "./components/UI/UserInteractionOverlay/UserInteractionOverlay";

import router from "./router";
import {
  toggleFullscreen,
  setFullscreenState,
} from "./redux/slices/settingSlice";
import "./assets/css/App.scss";
import ToastContainer from "./components/UI/ToastContainer/ToastContainer";
import { installLocalStoragePatch } from "./utils/installLsPatch";
import { AudioInteractionContext } from "./context/AudioInteractionContext";
import useStartSyncWatcher from "./hooks/useStartSyncWatcher";
import useAudioInteractionCheck from "./hooks/useAudioInteractionCheck";

function App() {
  const dispatch = useDispatch();
  useStartSyncWatcher(2000);
  installLocalStoragePatch();

  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "F11") {
        e.preventDefault(); // Prevent default browser fullscreen
        dispatch(toggleFullscreen());
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [dispatch]);

  // 🔹 Sync Redux if fullscreen changes outside the app (e.g., user presses F11 manually)
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreen = !!document.fullscreenElement;
      dispatch(setFullscreenState(isFullscreen));
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, [dispatch]);

  const { needsInteraction, handleUserInteraction, ensureAudioReady } =
    useAudioInteractionCheck();

  const handleClose = async () => {
    await handleUserInteraction();
  };

  return (
    <AudioInteractionContext.Provider
      value={{ needsInteraction, ensureAudioReady, handleUserInteraction }}
    >
      <div className="app-wrapper">
        <div className="app-content">
          <RouterProvider router={router} />
          <ToastContainer />

          <UserInteractionOverlay
            onClose={handleClose}
            open={needsInteraction}
          />
        </div>
      </div>
    </AudioInteractionContext.Provider>
  );
}

export default App;
